/**
 * The generic interview step renderer (ONBOARDING-FLOW-SPEC.md S2) —
 * driven entirely by tree data, never by step id: chip steps render
 * Polaris chip buttons from the step's own `options`, every other kind
 * renders a text input. A tree version that adds a step ships with no
 * renderer change as long as its `kind` is one already handled here.
 *
 * `AnsweredStep` renders one past turn as chat history; `StepRenderer`
 * renders the live question. Both are exported from one file because
 * they are the two faces of the same step data.
 *
 * Spec #47 §2 adds three things, all of them data-driven and none of
 * them a new step semantic:
 *
 *  - the **why-line** (D6) under the question — the floor this answer is
 *    held to, in the agent's own words. A step that carries none renders
 *    none; the renderer never invents a constraint.
 *  - **option labels** (D6) for chips whose canonical value is not a
 *    presentable phrase, with `prettifyOption` still the fallback so a
 *    raw enum key never reaches a human either way.
 *  - a **refusal rendered as a chat turn**, sitting with the question,
 *    which stays live with its chips. A refusal is the agent answering,
 *    not an error that ate the step.
 */

import { useState } from 'react';
import { BlockStack, Button, Icon, InlineStack, Text, TextField } from '@shopify/polaris';

import { StoreIcon, AppsIcon, TeamIcon } from '@shopify/polaris-icons';
import type { TreeStep } from '../../services/onboardingApi';
import { describeAnswer, prettifyOption } from './treeWalk';

export function AnsweredStep({
  step,
  answer,
}: {
  step: TreeStep;
  answer: unknown;
}) {
  return (
    <BlockStack gap="100">
      <Text as="p" tone="subdued" variant="bodySm">
        {step.prompt}
      </Text>
      <Text as="p" variant="bodyMd" fontWeight="medium">
        {describeAnswer(step, answer)}
      </Text>
    </BlockStack>
  );
}

/** "3000 USD meta_ads, 1500 USD google_ads" -> platform entries;
 * "5000 USD" -> one blended entry. Unparseable segments are dropped
 * rather than thrown — the Continue button stays disabled on empty
 * results so the merchant sees nothing silently vanished. */
export function parseBudgetAnswer(raw: string): Array<Record<string, unknown>> {
  const entries: Array<Record<string, unknown>> = [];
  for (const segment of raw.split(',')) {
    const tokens = segment.trim().split(/\s+/).filter(Boolean);
    if (tokens.length < 2) continue;
    const amount = Number(tokens[0]);
    if (!Number.isFinite(amount)) continue;
    const currency = tokens[1].toUpperCase();
    const platform = tokens[2];
    entries.push(
      platform
        ? { scope: 'platform', platform: platform.toLowerCase(), amount, currency }
        : { scope: 'blended', amount, currency }
    );
  }
  return entries;
}

export function parseListAnswer(raw: string, maxItems: number | null): string[] {
  const items = raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return maxItems ? items.slice(0, maxItems) : items;
}

const CURRENCY_PATTERN = /^[A-Za-z]{3}$/;

/** The same shape `parseBudgetAnswer` produces, built from labelled fields
 * instead of a syntax the merchant has to memorise. */
export function buildBlendedBudget(
  amountText: string,
  currencyText: string
): Array<Record<string, unknown>> | null {
  const amount = Number(amountText.trim());
  if (amountText.trim() === '' || !Number.isFinite(amount) || amount < 0) return null;
  const currency = currencyText.trim();
  if (!CURRENCY_PATTERN.test(currency)) return null;
  return [{ scope: 'blended', amount, currency: currency.toUpperCase() }];
}

function typedHelpText(step: TreeStep): string | undefined {
  if (step.kind === 'list') {
    return `Separate with commas${step.max_items ? ` (up to ${step.max_items})` : ''}.`;
  }
  return undefined;
}

function parseTypedAnswer(step: TreeStep, raw: string): unknown {
  const trimmed = raw.trim();
  switch (step.kind) {
    case 'number':
      return Number(trimmed);
    case 'list':
      return parseListAnswer(raw, step.max_items);
    case 'budget':
      return parseBudgetAnswer(raw);
    default:
      return trimmed;
  }
}

function isSubmittable(step: TreeStep, raw: string): boolean {
  if (step.kind === 'number') return Number.isFinite(Number(raw.trim())) && raw.trim() !== '';
  const answer = parseTypedAnswer(step, raw);
  const hasContent = Array.isArray(answer) ? answer.length > 0 : String(answer).length > 0;
  return hasContent || !step.required;
}

/** The agent's own reason for asking, when the tree carries one. */
function WhyLine({ step }: { step: TreeStep }) {
  if (!step.why) return null;
  return (
    <Text as="p" variant="bodySm" tone="subdued">
      {step.why}
    </Text>
  );
}

/** A refusal, in the conversation, beside the question it refused. */
function RefusalTurn({ refusal }: { refusal?: string | null }) {
  if (!refusal) return null;
  return (
    <Text as="p" variant="bodySm" tone="critical">
      {refusal}
    </Text>
  );
}

function optionLabel(step: TreeStep, option: string): string {
  return step.option_labels?.[option] ?? prettifyOption(option);
}

/**
 * The budget question, asked the way a business owner already thinks about
 * it: an amount and a currency. The old single text field demanded the
 * merchant type `3000 USD meta_ads, 1500 USD google_ads` — a syntax that
 * existed only in the help text, and whose unparseable segments were
 * silently dropped.
 *
 * That per-platform split is still supported, because some merchants
 * genuinely budget that way, but it now lives behind an explicit
 * disclosure and reuses `parseBudgetAnswer` unchanged. When the split is
 * open and typed into, it is the answer; otherwise the labelled fields
 * are. Either way the server receives the same BudgetEntry array.
 */
function BudgetStep({ step, submitting, onSubmit, refusal }: StepRendererProps) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [split, setSplit] = useState('');
  const [splitOpen, setSplitOpen] = useState(false);

  const usingSplit = splitOpen && split.trim() !== '';
  const splitEntries = usingSplit ? parseBudgetAnswer(split) : [];
  // A split the merchant typed but we could not read must say so, not
  // quietly submit the half of it that happened to parse.
  const splitSegments = usingSplit ? split.split(',').filter((s) => s.trim() !== '').length : 0;
  const splitError = usingSplit && (splitEntries.length !== splitSegments || split.split(',').some(segment => { const parts = segment.trim().split(/\s+/); return parts.length !== 3 || buildBlendedBudget(parts[0], parts[1] ?? '') === null || !/^[a-z][a-z0-9_]*$/i.test(parts[2] ?? ''); }));

  const blended = buildBlendedBudget(amount, currency);
  const answer = usingSplit ? (splitError ? null : splitEntries) : blended;
  const canSubmit = !submitting && answer !== null && answer.length > 0;

  const submit = () => {
    if (!canSubmit || answer === null) return;
    onSubmit(answer);
  };

  const amountError =
    amount.trim() !== '' && buildBlendedBudget(amount, 'USD') === null
      ? 'Enter an amount of 0 or more.'
      : undefined;
  const currencyError =
    currency.trim() !== '' && !CURRENCY_PATTERN.test(currency.trim())
      ? 'Use a 3-letter currency code, like USD.'
      : undefined;

  return (
    <BlockStack gap="200">
      <Text as="p" variant="bodyMd">
        {step.prompt}
      </Text>
      <WhyLine step={step} />
      <RefusalTurn refusal={refusal} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <BlockStack gap="200">
          <div className="mk-budget-fields">
            <TextField
              label="Monthly budget"
              type="number"
              min={0}
              autoComplete="off"
              value={amount}
              onChange={setAmount}
              error={amountError}
              disabled={usingSplit}
            />
            <TextField
              label="Currency"
              autoComplete="off"
              maxLength={3}
              value={currency}
              onChange={setCurrency}
              error={currencyError}
              helpText="3-letter code"
              disabled={usingSplit}
            />
          </div>

          <details open={splitOpen} onToggle={(e) => setSplitOpen(e.currentTarget.open)}>
            <summary>Split the budget across platforms instead (optional)</summary>
            <TextField
              label="Per-platform budget"
              autoComplete="off"
              value={split}
              onChange={setSplit}
              helpText='One per platform, comma separated — for example: 3000 USD meta_ads, 1500 USD google_ads'
              error={
                splitError
                  ? 'Each entry needs an amount, a 3-letter currency and a platform, like "3000 USD meta_ads".'
                  : undefined
              }
            />
          </details>

          <InlineStack gap="200">
            <Button variant="primary" submit disabled={!canSubmit} loading={submitting}>
              Continue
            </Button>
            {!step.required && (
              <Button variant="plain" disabled={submitting} onClick={() => onSubmit([])}>
                Skip
              </Button>
            )}
          </InlineStack>
        </BlockStack>
      </form>
    </BlockStack>
  );
}

interface StepRendererProps {
  step: TreeStep;
  submitting: boolean;
  onSubmit: (answer: unknown) => void;
  /** The server's last refusal for THIS step, rendered as a turn. */
  refusal?: string | null;
}

/** The live, answerable question. Render with `key={step.id}` from the
 * caller so a fresh step always starts from a clean, empty input. */
export function StepRenderer({
  step,
  submitting,
  onSubmit,
  refusal,
}: StepRendererProps) {
  const [text, setText] = useState('');

  if (step.kind === 'budget') return <BudgetStep step={step} submitting={submitting} onSubmit={onSubmit} refusal={refusal} />;

  if (step.id === 'vertical' && step.kind === 'chips') {
    const choices: Record<string, {label: string; detail: string; icon: typeof StoreIcon}> = {
      shopify_merchant: {label: 'Shopify store', detail: 'Understand your sales, ads, and customer marketing.', icon: StoreIcon},
      b2b_saas: {label: 'B2B software business', detail: 'See what brings in leads and grows your pipeline.', icon: AppsIcon},
      agency: {label: 'Marketing agency', detail: 'Bring your clients’ marketing work into one place.', icon: TeamIcon},
    };
    return <BlockStack gap="300">
      <Text as="h2" variant="headingLg">What kind of business do you run?</Text>
      <Text as="p" tone="subdued">Choose the closest match to personalize your setup.</Text>
      <RefusalTurn refusal={refusal} />
      <div className="mk-business-options">
        {step.options.map(option => {
          const choice = choices[option];
          return <button className="mk-business-option" key={option} disabled={submitting} onClick={() => onSubmit(option)}>
            {choice && <span className="mk-business-icon"><Icon source={choice.icon} /></span>}
            <span><strong>{choice?.label ?? optionLabel(step, option)}</strong>{choice && <small>{choice.detail}</small>}</span>
            <span className="mk-business-arrow" aria-hidden="true">→</span>
          </button>;
        })}
      </div>
    </BlockStack>;
  }

  if (step.kind === 'chips') {
    return (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          {step.prompt}
        </Text>
        <WhyLine step={step} />
        <RefusalTurn refusal={refusal} />
        <InlineStack gap="200" wrap>
          {step.options.map((option) => (
            <Button key={option} disabled={submitting} onClick={() => onSubmit(option)}>
              {optionLabel(step, option)}
            </Button>
          ))}
        </InlineStack>
      </BlockStack>
    );
  }

  const submit = () => {
    if (submitting || !isSubmittable(step, text)) return;
    onSubmit(parseTypedAnswer(step, text));
  };

  return (
    <BlockStack gap="200">
      <Text as="p" variant="bodyMd">
        {step.prompt}
      </Text>
      <WhyLine step={step} />
      <RefusalTurn refusal={refusal} />
      <form onSubmit={event => { event.preventDefault(); submit(); }}>
      <TextField
        label={step.prompt}
        labelHidden
        autoComplete="off"
        type={step.kind === 'number' ? 'number' : 'text'}
        value={text}
        onChange={setText}
        helpText={typedHelpText(step)}
        connectedRight={
          <Button
            variant="primary"
            disabled={submitting || !isSubmittable(step, text)}
            submit
          >
            Continue
          </Button>
        }
      />
      </form>
      {!step.required && (
        <Button
          variant="plain"
          disabled={submitting}
          onClick={() => onSubmit(step.kind === 'text' ? '' : [])}
        >
          Skip
        </Button>
      )}
    </BlockStack>
  );
}
